import { ISignable } from "@/mixins/SignalsMixin/interfaces";
import { SignableType } from "@/services/DTOs/SignalDetail";
import { BioLocation, IBioLocation } from "./BioLocation";
import { CompletionStage } from "./CompetionStage";
import { Flags, IFlags, TalentSeekerOnboardedStatus } from "./Flags";
import { IStats, Stats } from "./Stats";

// tslint:disable-next-line interface-over-type-literal
export type FeaturesReachValueFlags = {
  candidate_suggestions_from_torre: string;
  meeting_links: string
};

export interface IPerson {
  id: string | undefined;
  ggId: string;
  subjectId: number | undefined;
  publicId: string;
  username: string | undefined;
  name: string;
  email: string;
  hasEmail: boolean;
  hasBio: boolean;
  bioCompletion: number;
  bioCompletionStage: CompletionStage;
  weight: number;
  verified: boolean;
  phone?: string;
  picture: string;
  pictureThumbnail: string;
  professionalHeadline: string;
  theme: string;
  created?: string;
  stats: IStats;
  flags: IFlags;
  canary: boolean;
  location: IBioLocation | undefined;
  veiled?: boolean; // True when the user is marked as a scammer.
  locale: string | undefined;
  isTester: boolean;
}

export class Person implements ISignable {

  public static Anonymous(): Person {
    return new Person({
      id: "-1",
      ggId: "-1",
      subjectId: -1,
      publicId: "",
      username: "anonymous",
      name: "Anonymous",
      email: "",
      hasEmail: false,
      hasBio: false,
      bioCompletion: 0,
      bioCompletionStage: new CompletionStage({ stage: 1, progress: 0 }),
      weight: 0,
      verified: false,
      picture: "",
      pictureThumbnail: "",
      professionalHeadline: "",
      theme: "default",
      stats: {
        experiences: 0,
        opportunities: 0,
        potentialMatches: 0,
        verifiedExperiences: 0
      },
      flags: {
        experiencesVerificationsSurvey: false,
        firstGetSignalBenefitsViewed: false,
        firstSignalOnBoarded: false,
        firstSignalSent: false,
        matchAndRankDisclaimerViewed: false,
        membersBenefitsViewed: false,
        notificationsPhrasesSarcastic: false,
        opportunitiesBenefitsViewed: false,
        torreMatchAcknowledged: false,
        automessagesMeetingsButtonViewed: false,
        referredMatchUnderstood: false,
        signedInToOpportunities: false,
        contactsImported: false,
        opportunityOperator: false,
        opportunityCrawler: false,
        veiled: false,
        earlyAccess: false,
        talentSeekerOnboarded: TalentSeekerOnboardedStatus.UNKNOWN,
        onboardingBenefitsViewed: false
      },
      canary: false,
      location: undefined,
      locale: undefined,
      isTester: false
    });
  }

  public static Test(): Person {
    return new Person({
      id: "-1",
      ggId: "-1",
      subjectId: -1,
      publicId: "testUser",
      username: "testUser",
      name: "Test User",
      email: "tester@gmail.com",
      hasEmail: true,
      hasBio: false,
      bioCompletion: 0,
      bioCompletionStage: new CompletionStage({ stage: 1, progress: 0 }),
      weight: 0,
      verified: false,
      picture: "",
      pictureThumbnail: "pictureThumbnail",
      professionalHeadline: "Test user",
      theme: "default",
      stats: {
        experiences: 0,
        opportunities: 0,
        potentialMatches: 0,
        verifiedExperiences: 0
      },
      flags: {
        experiencesVerificationsSurvey: false,
        firstGetSignalBenefitsViewed: false,
        firstSignalOnBoarded: false,
        firstSignalSent: false,
        matchAndRankDisclaimerViewed: false,
        membersBenefitsViewed: false,
        notificationsPhrasesSarcastic: false,
        opportunitiesBenefitsViewed: false,
        torreMatchAcknowledged: false,
        automessagesMeetingsButtonViewed: false,
        referredMatchUnderstood: false,
        signedInToOpportunities: false,
        contactsImported: false,
        opportunityOperator: false,
        opportunityCrawler: false,
        veiled: false,
        earlyAccess: false,
        talentSeekerOnboarded: TalentSeekerOnboardedStatus.UNKNOWN,
        onboardingBenefitsViewed: false
      },
      canary: false,
      location: undefined,
      locale: undefined,
      isTester: false
    });
  }

  public id: string | undefined;
  public ggId: string;
  public subjectId: number | undefined;
  public username: string | undefined;
  public publicId: string;
  public name: string;
  public email: string;
  public hasEmail: boolean;
  public hasBio: boolean;
  public bioCompletion: number;
  public bioCompletionStage: CompletionStage;
  public weight: number;
  public verified: boolean;
  public phone?: string;
  public picture: string;
  public pictureThumbnail: string;
  public professionalHeadline: string;
  public theme: string;
  public created?: string;
  public stats: Stats;
  public flags: Flags;
  public canary: boolean;
  public location: BioLocation | undefined;
  public veiled?: boolean;
  public locale: string | undefined;
  public isTester: boolean;

  constructor(data: IPerson) {
    this.id = data.id;
    this.ggId = data.ggId;
    this.subjectId = data.subjectId;
    this.username = data.username;
    this.publicId = data.publicId;
    this.name = data.name;
    this.email = data.email;
    this.hasEmail = data.hasEmail;
    this.hasBio = data.hasBio;
    this.bioCompletion = data.bioCompletion;
    this.bioCompletionStage = data.bioCompletionStage;
    this.weight = data.weight;
    this.verified = data.verified;
    this.phone = data.phone;
    this.picture = data.picture;
    this.pictureThumbnail = data.pictureThumbnail;
    this.professionalHeadline = data.professionalHeadline;
    this.theme = data.theme;
    this.created = data.created;
    this.stats = data.stats;
    this.flags = data.flags;
    this.canary = data.canary;
    this.location = data.location;
    this.veiled = data.veiled;
    this.locale = data.locale;
    this.isTester = /.*\+test.*@torre\.co$/.test(data.email);
  }

  public firstName(): string {
    return this.name ? this.name.replace(/ .*/, "") : "";
  }

  public signableId(): string | undefined {
    return this.username;
  }

  public signableType(): SignableType {
    return SignableType.Person;
  }

  public isOperator(): boolean {
    return this.flags ? this.flags.opportunityOperator : false;
  }
}
